import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField as MuiTextField,
} from "@mui/material";
import { Field, FieldArray, useFormikContext, FastField } from "formik";
import BaustellenSelect from "../baustellen/BaustellenSelect";
import { TextField } from "formik-mui";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Baustelle, Benutzer } from "../../api";
import {
  useDeleteZugewieseneBaustelle,
  useUpdateZugewieseneBaustelle,
} from "../../client/hooks";
import BaustellenEintragDeponien from "./BaustellenEintragDeponien";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  memo,
  useCallback,
} from "react";
import { useEingangsrechnungContext } from "../../contexts/EingangsrechnungContext";
import { useBaustellenEintragContext } from "../../contexts/BaustellenEintragContext";
import { formatEuroDe } from "../../utils/fomatters";
import AngestelltenSelect from "../benutzer/AngestelltenSelect";

export interface ZugewieseneBaustelleUpdated {
  id: string;
  rechnungsId: string | undefined;
  baustelle: any | undefined;
  pruefer: any | undefined;
  pruefStatus: string | undefined;
  betrag: string;
  leistung: string | undefined;
}

const BaustellenEintragItem = memo(
  ({
    baustelle,
    index,
    rechnungId,
    deleteZuweisung,
    remove,
    updateBeleg,
    saveZuweisung,
    bezahltAm,
  }: {
    baustelle: ZugewieseneBaustelleUpdated;
    index: number;
    rechnungId: string;
    deleteZuweisung: (
      rId: string,
      id: string,
      index: number,
      remove: (index: number) => void
    ) => Promise<void>;
    remove: (index: number) => void;
    updateBeleg: () => Promise<void>;
    saveZuweisung: (
      rechnungId: string,
      baustelle: ZugewieseneBaustelleUpdated,
      status: string
    ) => Promise<void>;
    bezahltAm: any;
  }) => {
    const { setFieldValue } = useFormikContext();
    const { updateBetrag } = useBaustellenEintragContext();
    const { getCheckedDeponien } = useEingangsrechnungContext();

    const assignedDeponieSum = useMemo(() => {
      const checkedDeponien = getCheckedDeponien(index);
      return checkedDeponien.reduce(
        (sum, item) => sum + (item.preisGesamt ?? 0),
        0
      );
    }, [getCheckedDeponien, index]);

    const [localBetrag, setLocalBetrag] = useState(baustelle.betrag);
    const [localLeistung, setLocalLeistung] = useState(
      baustelle.leistung ?? ""
    );
    const betragTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const leistungTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const deponieHelperText = `Zugeordnet [€]: ${formatEuroDe(
      assignedDeponieSum
    )}`;

    const changeNettoBetrag = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalBetrag(value);
        if (betragTimerRef.current) clearTimeout(betragTimerRef.current);
        betragTimerRef.current = setTimeout(() => {
          const formattedValue = parseFloat(
            String(value).replace(".", "").replace(",", ".")
          );
          setFieldValue(`baustellenEintrag.${index}.betrag`, value);
          updateBetrag(index, formattedValue);
        }, 300);
      },
      [index, setFieldValue, updateBetrag]
    );

    const changeLeistung = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalLeistung(value);
        if (leistungTimerRef.current) clearTimeout(leistungTimerRef.current);
        leistungTimerRef.current = setTimeout(() => {
          setFieldValue(`baustellenEintrag.${index}.leistung`, value);
        }, 300);
      },
      [index, setFieldValue]
    );

    useEffect(() => {
      setLocalBetrag(baustelle.betrag);
    }, [baustelle.betrag]);

    useEffect(() => {
      setLocalLeistung(baustelle.leistung ?? "");
    }, [baustelle.leistung]);

    return (
      <React.Fragment>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: ["1fr", "2fr  1fr 1fr"],
            opacity: 1,
            pointerEvents: "auto",
            background: "#FFFFFF",
          }}
        >
          <FastField
            component={BaustellenSelect}
            name={`baustellenEintrag.${index}.baustelle`}
            label={"Baustelle"}
            required={true}
          ></FastField>
          <FastField
            component={AngestelltenSelect}
            name={`baustellenEintrag.${index}.pruefer`}
            label={"Prüfer"}
            required={true}
          ></FastField>
          <Field
            component={TextField}
            name={`baustellenEintrag.${index}.betrag`}
            label={"Nettobetrag in €"}
            required={true}
            inputProps={{ style: { textAlign: "right" } }}
            value={localBetrag}
            onChange={changeNettoBetrag}
            helperText={deponieHelperText}
          ></Field>
          <MuiTextField
            label={"Leistung"}
            value={localLeistung}
            onChange={changeLeistung}
            fullWidth
          />
          {baustelle.pruefStatus !== "offen" ? (
            <FastField
              component={TextField}
              name={`baustellenEintrag.${index}.pruefStatus`}
              label={"Prüfstatus"}
              inputProps={{ readOnly: true }}
            ></FastField>
          ) : (
            <></>
          )}
          <IconButton
            sx={{ width: "30px" }}
            onClick={async () => {
              await deleteZuweisung(rechnungId, baustelle.id, index, remove);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
        <Box sx={{ mt: 2 }}>
          <BaustellenEintragDeponien
            baustelleId={(baustelle.baustelle as Baustelle)?.id ?? ""}
            rechnungId={rechnungId}
            index={index}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end", // Aligns button to the right
            mt: 2,
          }}
        >
          {baustelle.pruefStatus === "in Prüfung" ? (
            <>
              <Button
                variant={"outlined"}
                sx={{ height: "70%" }}
                onClick={() => {
                  setFieldValue(
                    `baustellenEintrag.${index}.pruefStatus`,
                    "abgelehnt"
                  );
                  saveZuweisung(rechnungId, baustelle, "abgelehnt");
                }}
              >
                Ablehnen
              </Button>
              <Button
                variant={"contained"}
                sx={{ height: "70%", ml: 2 }}
                color={"secondary"}
                onClick={async () => {
                  await updateBeleg();
                  setFieldValue(
                    `baustellenEintrag.${index}.pruefStatus`,
                    "freigegeben"
                  );
                  saveZuweisung(rechnungId, baustelle, "freigegeben");
                }}
              >
                Freigeben
              </Button>
            </>
          ) : baustelle.pruefStatus === "freigegeben" ? (
            <Button
              variant={"outlined"}
              sx={{ height: "70%" }}
              color={"success"}
              onClick={() => {
                setFieldValue(
                  `baustellenEintrag.${index}.pruefStatus`,
                  "in Prüfung"
                );
                saveZuweisung(rechnungId, baustelle, "in Prüfung");
              }}
            >
              Freigegeben
            </Button>
          ) : baustelle.pruefStatus === "abgelehnt" ? (
            <Button
              variant={"outlined"}
              sx={{ height: "70%" }}
              color={"error"}
              onClick={() => {
                setFieldValue(
                  `baustellenEintrag.${index}.pruefStatus`,
                  "in Prüfung"
                );
                saveZuweisung(rechnungId, baustelle, "in Prüfung");
              }}
            >
              Abgelehnt
            </Button>
          ) : (
            <></>
          )}
        </Box>
        <Divider sx={{ mt: 2, mb: 2 }}></Divider>
      </React.Fragment>
    );
  }
);

export default function BaustellenEintrag({
  baustellenEintraege,
  rechnungId,
  dirty,
  isValid,
  isValidating,
  values,
  kundennummer,
  updateBeleg,
}: {
  baustellenEintraege: ZugewieseneBaustelleUpdated[];
  rechnungId: string;
  dirty: boolean;
  isValid: boolean;
  isValidating: boolean;
  values: any;
  kundennummer: string;
  updateBeleg: (values: any) => Promise<void>;
}) {
  const { setFieldValue } = useFormikContext();
  const deleteZuweisung = useDeleteZugewieseneBaustelle();
  const updateZuweisung = useUpdateZugewieseneBaustelle();
  const { updateBetrag, initializeBetragValues } =
    useBaustellenEintragContext();

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const removeRef = useRef<((index: number) => void) | null>(null);
  const pushRef = useRef<((obj: any) => void) | null>(null);

  const stableRemove = useCallback((index: number) => {
    removeRef.current?.(index);
  }, []);

  const stablePush = useCallback((obj: any) => {
    pushRef.current?.(obj);
  }, []);

  useEffect(() => {
    if (baustellenEintraege && baustellenEintraege.length > 0) {
      initializeBetragValues(baustellenEintraege);
    }
  }, [baustellenEintraege.length, initializeBetragValues]);

  const saveZuweisungCallback = useCallback(
    async (
      rechnungId: string,
      baustelle: ZugewieseneBaustelleUpdated,
      status: string
    ) => {
      if (baustelle.id) {
        const formattedBetrag = parseFloat(
          String(baustelle.betrag).replace(".", "").replace(",", ".")
        );
        const prueferId = (baustelle.pruefer as Benutzer)?.id ?? null;
        const baustellenId = (baustelle.baustelle as Baustelle)?.id ?? null;
        updateZuweisung(
          rechnungId,
          baustelle.id,
          { rechnungBeglichenAm: values.bezahltAm },
          {
            baustellenId: baustellenId,
            prueferId: prueferId,
            leistung: baustelle.leistung,
            betrag: formattedBetrag,
            pruefStatus: status,
          }
        );

        const newEntries = baustellenEintraege.map((item) => {
          if (item.id === baustelle.id) {
            return { ...item, pruefStatus: status };
          }
          return item;
        });

        const anyAbgelehnt = newEntries.some(
          (item) => item.pruefStatus === "abgelehnt"
        );

        if (anyAbgelehnt) {
          setFieldValue("pruefStatus", "GEPRUEFT_NICHT_OK");
        } else {
          const allFreigegeben = newEntries.every(
            (item) => item.pruefStatus === "freigegeben"
          );
          if (allFreigegeben) {
            setFieldValue("pruefStatus", "GEPRUEFT_OK");
          }
        }
      }
    },
    [
      baustellenEintraege,
      rechnungId,
      setFieldValue,
      updateZuweisung,
      values.bezahltAm,
    ]
  );

  const deleteZuweisungCallback = useCallback(
    async (
      rId: string,
      id: string,
      index: number,
      remove: (index: number) => void
    ) => {
      if (id) {
        await deleteZuweisung(rId, id);
      }
      remove(index);
    },
    [deleteZuweisung]
  );

  const updateBelegCallback = useCallback(async () => {
    await updateBeleg(valuesRef.current);
  }, [updateBeleg]);

  return (
    <FieldArray name="baustellenEintrag">
      {({ push, remove }) => {
        // Refs mit aktuellen Funktionen befüllen – kein Re-render
        removeRef.current = remove;
        pushRef.current = push;
        return (
          <>
            {baustellenEintraege?.map((baustelle, index) => (
              <BaustellenEintragItem
                key={baustelle.id || `baustelle-${index}`}
                baustelle={baustelle}
                index={index}
                rechnungId={rechnungId}
                deleteZuweisung={deleteZuweisungCallback}
                remove={stableRemove}
                updateBeleg={updateBelegCallback}
                saveZuweisung={saveZuweisungCallback}
                bezahltAm={values.bezahltAm}
              />
            ))}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                color={"secondary"}
                onClick={() => {
                  setFieldValue("pruefStatus", "OFFEN");
                  stablePush({
                    id: "",
                    rechnungId: rechnungId,
                    baustelle: "",
                    pruefer: "",
                    pruefStatus: "offen",
                  });
                }}
              >
                <AddIcon />
              </Button>
            </Box>
          </>
        );
      }}
    </FieldArray>
  );
}
